/**
 * PulseSettingsCard — Pulse source curation panel.
 *
 * Drops into SettingsPage.tsx as the "Pulse" tab. Mirrors the AISettingsCard
 * pattern. Calls the backend admin endpoint:
 *
 *   POST /api/v1/admin/bets/:id/sources   (body: { url, cache_ttl_hours? })
 *
 * Backend lives in versusapp routes/adminPulse.ts and is wired against the X
 * bearer stored in AWS Secrets Manager (versus/staging/pulse-x-api-bearer).
 * The bearer never reaches the browser.
 *
 * Roles allowed by backend: master | content | compliance.
 */

import React, { useState } from 'react';
import { apiService } from '../services/apiService';
import './PulseSettingsCard.css';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface IngestedResult {
  provider: 'X' | 'REDDIT';
  external_id: string;
  external_url: string;
  author_handle: string;
  status: 'new' | 'updated';
}

const PulseSettingsCard: React.FC = () => {
  const [betId, setBetId] = useState('');
  const [url, setUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<IngestedResult | null>(null);

  const trimmedBetId = betId.trim();
  const trimmedUrl = url.trim();

  const uuidValid = UUID_RE.test(trimmedBetId);
  const urlValid =
    trimmedUrl.length >= 5 &&
    (trimmedUrl.includes('x.com/') ||
      trimmedUrl.includes('twitter.com/') ||
      trimmedUrl.includes('reddit.com/'));

  const canSubmit = uuidValid && urlValid && !submitting;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setLastResult(null);
    try {
      const res = await apiService.attachPulseSource(trimmedBetId, trimmedUrl);
      setLastResult(res.ingested);
      setUrl('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Attach failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card">
      <div className="card-head">
        <span className="card-title">📡 Pulse Source Aggregation</span>
      </div>
      <div className="card-body">
        <p className="pulse-intro">
          Paste a tweet or reddit URL to attach it as a Pulse source for a specific bet. The
          backend fetches the live post via the platform's official API and persists it. The
          mobile app's Pulse panel will then show the post under that bet.
        </p>

        {error && <div className="pulse-error">{error}</div>}

        {lastResult && (
          <div className="pulse-success">
            <div className="pulse-success-title">
              ✓ {lastResult.status === 'new' ? 'Attached' : 'Updated'}
              {' · '}
              <span className="pulse-success-provider">{lastResult.provider}</span>
            </div>
            <div className="pulse-success-meta">
              <span>@{lastResult.author_handle}</span>
              {' · '}
              <a
                href={lastResult.external_url}
                target="_blank"
                rel="noreferrer noopener"
                className="pulse-success-link"
              >
                {lastResult.external_id}
              </a>
            </div>
          </div>
        )}

        <form onSubmit={onSubmit}>
          <div className="form-group">
            <label className="form-label">Bet UUID</label>
            <input
              type="text"
              className="form-input"
              value={betId}
              onChange={(e) => setBetId(e.target.value)}
              placeholder="18a73fb2-8406-4038-bc45-200f6b9695e9"
              autoComplete="off"
              spellCheck={false}
            />
            <small className="form-hint">
              {trimmedBetId.length === 0
                ? 'UUID of the market/bet to attach the source to.'
                : uuidValid
                ? ' '
                : 'Not a valid UUID.'}
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">Source URL</label>
            <input
              type="url"
              className="form-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://x.com/AP/status/2053453751239967085"
              autoComplete="off"
              spellCheck={false}
            />
            <small className="form-hint">
              {trimmedUrl.length === 0
                ? 'Supported: x.com / twitter.com / reddit.com'
                : urlValid
                ? ' '
                : 'URL must contain x.com/, twitter.com/, or reddit.com/.'}
            </small>
          </div>

          <div className="pulse-submit-row">
            <small className="form-hint">
              Default cache TTL: 24 hours. Provider chosen from URL automatically.
            </small>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!canSubmit}
            >
              {submitting ? 'Attaching…' : 'Attach Source'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PulseSettingsCard;
